/**
 * Universal tracking tags — operator page (feature #161).
 *
 * Surface for the /api/v1/universal-tag backend. One tag template can serve
 * click, impression and conversion tracking — the operator embeds a single
 * snippet and the server disambiguates via ?event=. Two panels:
 *
 *   1. Provision form — name, event whitelist (checkbox trio), optional
 *      campaign scope. Name/events validated client-side (mirrors server).
 *      On success the UI surfaces the generated `tag_key` plus three
 *      copy-paste snippets (img pixel, anchor click, conversion JS).
 *
 *   2. Tag list — one row per provisioned tag. Each row shows name, key,
 *      enabled events (colored badges), enabled flag, and actions: copy
 *      snippet, toggle enabled, delete.
 *
 * Brand: emerald → sky gradient headline, Inter, zinc borders, 6-8px rounded,
 * light theme only. No emojis, no competitor names.
 *
 * The file is intentionally monolithic — breaking sub-components into
 * sibling files would widen the owned-file surface. Internal widgets live
 * here as local function components.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiBaseUrl } from "@/api-client";
import {
	buildTagUrl,
	canonicalEventOrder,
	clickSnippet,
	conversionSnippet,
	createTag,
	deleteTag,
	EMPTY_DRAFT,
	EVENT_DESCRIPTIONS,
	EVENT_LABELS,
	eventBadgeClass,
	formatFiredAt,
	impressionSnippet,
	listEvents,
	listTags,
	replaceInList,
	sortTags,
	tagDraftSchema,
	UNIVERSAL_TAG_EVENTS,
	universalTagQueryKeys,
	updateTag,
	type TagDraft,
	type UniversalTagEventKind,
	type UniversalTagEventView,
	type UniversalTagView,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens (match brand rules)
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const CHECKBOX_ROW =
	"flex items-start gap-2 rounded-[6px] border border-zinc-200 bg-white p-3 text-sm text-zinc-700";
const CODE_BLOCK =
	"rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-800 whitespace-pre-wrap break-all";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface UniversalTagPageProps {
	/** Testing seam — override the base used for snippet URLs. */
	baseOverride?: string;
}

export function UniversalTagPage({ baseOverride }: UniversalTagPageProps = {}) {
	const queryClient = useQueryClient();

	// Strip "/api/v1" suffix from apiBaseUrl so buildTagUrl can add the full
	// path itself. `apiBaseUrl` is "…/api/v1" by default.
	const snippetBase = useMemo(() => {
		const raw = baseOverride ?? apiBaseUrl;
		return raw.replace(/\/api\/v1\/?$/, "");
	}, [baseOverride]);

	const [draft, setDraft] = useState<TagDraft>({ ...EMPTY_DRAFT });
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [selectedTagId, setSelectedTagId] = useState<string | null>(null);

	const listQuery = useQuery({
		queryKey: universalTagQueryKeys.list(),
		queryFn: listTags,
	});

	const eventsQuery = useQuery({
		queryKey: selectedTagId
			? universalTagQueryKeys.events(selectedTagId)
			: ["universal-tag", "events", "__none__"],
		queryFn: () => (selectedTagId ? listEvents(selectedTagId, 50) : Promise.resolve<UniversalTagEventView[]>([])),
		enabled: selectedTagId !== null,
	});

	const createMutation = useMutation({
		mutationFn: createTag,
		onSuccess: (row) => {
			toast.success(`Tag "${row.name}" created.`);
			setDraft({ ...EMPTY_DRAFT });
			setDraftErrors({});
			setSelectedTagId(row.id);
			queryClient.setQueryData<UniversalTagView[]>(
				universalTagQueryKeys.list(),
				(prev) => (prev ? replaceInList(prev, row) : [row]),
			);
			queryClient.invalidateQueries({ queryKey: universalTagQueryKeys.list() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to create tag.");
		},
	});

	const updateMutation = useMutation({
		mutationFn: (args: { id: string; patch: Parameters<typeof updateTag>[1] }) =>
			updateTag(args.id, args.patch),
		onSuccess: (row) => {
			toast.success(`Tag "${row.name}" updated.`);
			queryClient.setQueryData<UniversalTagView[]>(
				universalTagQueryKeys.list(),
				(prev) => (prev ? replaceInList(prev, row) : [row]),
			);
			queryClient.invalidateQueries({ queryKey: universalTagQueryKeys.list() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to update tag.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteTag(id),
		onSuccess: (_void, id) => {
			toast.success("Tag removed.");
			if (selectedTagId === id) setSelectedTagId(null);
			queryClient.setQueryData<UniversalTagView[]>(
				universalTagQueryKeys.list(),
				(prev) => (prev ? prev.filter((t) => t.id !== id) : prev),
			);
			queryClient.invalidateQueries({ queryKey: universalTagQueryKeys.list() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove tag.");
		},
	});

	const sortedTags = useMemo(
		() => (listQuery.data ? sortTags(listQuery.data) : []),
		[listQuery.data],
	);

	const selectedTag = useMemo(
		() =>
			sortedTags.find((t) => t.id === selectedTagId) ??
			(listQuery.data?.find((t) => t.id === selectedTagId) ?? null),
		[sortedTags, listQuery.data, selectedTagId],
	);

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = tagDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data);
	}

	function toggleDraftEvent(evt: UniversalTagEventKind) {
		setDraft((prev) => {
			const has = prev.events.includes(evt);
			const events = has
				? prev.events.filter((e) => e !== evt)
				: canonicalEventOrder([...prev.events, evt]);
			return { ...prev, events };
		});
	}

	async function copyToClipboard(text: string, what: string) {
		try {
			if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
				toast.success(`${what} copied to clipboard.`);
			} else {
				toast.error("Clipboard not available in this browser.");
			}
		} catch (err) {
			toast.error((err as Error).message || "Copy failed.");
		}
	}

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 pb-16 pt-6">
			<header className="flex flex-col gap-2">
				<h1 className="font-inter bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
					Universal tracking tags
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Provision one tag per surface — landing page, ad unit, confirmation
					screen — and use the <span className="font-mono">?event=</span>{" "}
					parameter to distinguish clicks, impressions and conversions. One
					snippet per page, three signals behind it.
				</p>
			</header>

			<section aria-labelledby="create-tag-heading" className={`${CARD} p-6`}>
				<h2
					id="create-tag-heading"
					className="text-base font-semibold text-zinc-900"
				>
					Provision a tag
				</h2>
				<form
					onSubmit={handleSubmit}
					className="mt-4 flex flex-col gap-4"
				>
					<div className="flex flex-col gap-1">
						<label htmlFor="name" className={LABEL}>
							Tag name
						</label>
						<input
							id="name"
							name="name"
							type="text"
							className={INPUT}
							placeholder="Homepage hero unit"
							value={draft.name}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, name: e.target.value }))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
							maxLength={140}
						/>
						{draftErrors.name ? (
							<span className={ERROR_TEXT}>{draftErrors.name}</span>
						) : (
							<span className={HINT}>
								Operator-facing label — not shown to partners or end users.
							</span>
						)}
					</div>

					<div className="flex flex-col gap-1">
						<span className={LABEL}>Events to accept</span>
						<div className="mt-1 grid gap-3 md:grid-cols-3">
							{UNIVERSAL_TAG_EVENTS.map((evt) => {
								const checked = draft.events.includes(evt);
								return (
									<label
										key={evt}
										className={`${CHECKBOX_ROW} ${checked ? "border-emerald-300 bg-emerald-50/50" : ""}`}
									>
										<input
											type="checkbox"
											className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
											checked={checked}
											onChange={() => toggleDraftEvent(evt)}
											disabled={createMutation.isPending}
										/>
										<span className="flex flex-col">
											<span className="text-sm font-medium text-zinc-900">
												{EVENT_LABELS[evt]}
											</span>
											<span className={HINT}>{EVENT_DESCRIPTIONS[evt]}</span>
										</span>
									</label>
								);
							})}
						</div>
						{draftErrors.events ? (
							<span className={ERROR_TEXT}>{draftErrors.events}</span>
						) : null}
					</div>

					<div className="flex flex-col gap-1 md:max-w-sm">
						<label htmlFor="campaignId" className={LABEL}>
							Campaign (optional)
						</label>
						<input
							id="campaignId"
							name="campaignId"
							type="text"
							className={INPUT}
							placeholder="camp_summer_24"
							value={draft.campaignId}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, campaignId: e.target.value }))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
						/>
						{draftErrors.campaignId ? (
							<span className={ERROR_TEXT}>{draftErrors.campaignId}</span>
						) : (
							<span className={HINT}>
								Scope events to a specific campaign for easier reporting.
							</span>
						)}
					</div>

					<div>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMutation.isPending}
						>
							{createMutation.isPending ? "Creating…" : "Create tag"}
						</button>
					</div>
				</form>
			</section>

			<section aria-labelledby="tags-heading" className={`${CARD} p-6`}>
				<div className="flex items-center justify-between">
					<h2
						id="tags-heading"
						className="text-base font-semibold text-zinc-900"
					>
						Provisioned tags
					</h2>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
					>
						{listQuery.isFetching ? "Refreshing…" : "Refresh"}
					</button>
				</div>

				<div className="mt-4 overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
								<th className="px-4 py-2 font-medium">Name</th>
								<th className="px-4 py-2 font-medium">Key</th>
								<th className="px-4 py-2 font-medium">Events</th>
								<th className="px-4 py-2 font-medium">Status</th>
								<th className="px-4 py-2 font-medium text-right">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{listQuery.isLoading ? (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										Loading tags…
									</td>
								</tr>
							) : listQuery.isError ? (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-sm text-red-600"
									>
										{(listQuery.error as Error | undefined)?.message ??
											"Unable to load tags."}
									</td>
								</tr>
							) : sortedTags.length === 0 ? (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										No tags provisioned yet.
									</td>
								</tr>
							) : (
								sortedTags.map((t) => (
									<tr
										key={t.id}
										className={t.id === selectedTagId ? "bg-emerald-50/40" : ""}
									>
										<td className="px-4 py-3 font-medium text-zinc-900">
											{t.name}
											{t.campaign_id ? (
												<div className={HINT}>
													Campaign:{" "}
													<span className="font-mono">{t.campaign_id}</span>
												</div>
											) : null}
										</td>
										<td className="break-all px-4 py-3 font-mono text-xs text-zinc-700">
											{t.tag_key}
										</td>
										<td className="px-4 py-3">
											<div className="flex flex-wrap gap-1">
												{canonicalEventOrder(t.events).map((e) => (
													<span
														key={e}
														className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${eventBadgeClass(e)}`}
													>
														{EVENT_LABELS[e]}
													</span>
												))}
											</div>
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${t.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}
											>
												{t.enabled ? "Enabled" : "Paused"}
											</span>
										</td>
										<td className="px-4 py-3 text-right">
											<div className="inline-flex gap-2">
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() => setSelectedTagId(t.id)}
												>
													{selectedTagId === t.id ? "Viewing" : "View"}
												</button>
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() =>
														updateMutation.mutate({
															id: t.id,
															patch: { enabled: !t.enabled },
														})
													}
													disabled={updateMutation.isPending}
												>
													{t.enabled ? "Pause" : "Resume"}
												</button>
												<button
													type="button"
													className={BTN_DANGER}
													onClick={() => {
														if (
															typeof window !== "undefined" &&
															!window.confirm(
																`Remove tag "${t.name}"? Existing embeds will stop firing.`,
															)
														) {
															return;
														}
														deleteMutation.mutate(t.id);
													}}
													disabled={deleteMutation.isPending}
												>
													Remove
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</section>

			{selectedTag ? (
				<section
					aria-labelledby="snippet-heading"
					className={`${CARD} p-6`}
				>
					<h2
						id="snippet-heading"
						className="text-base font-semibold text-zinc-900"
					>
						Embed snippets for <span className="font-mono">{selectedTag.tag_key}</span>
					</h2>
					<p className={`${HINT} mt-1`}>
						Copy the snippet that matches where you're placing the tag. The
						server disambiguates clicks, impressions and conversions from the
						same key using the <span className="font-mono">event</span> query
						param.
					</p>

					<SnippetRow
						title="Impression (1x1 pixel)"
						enabled={selectedTag.events.includes("impression")}
						snippet={impressionSnippet(snippetBase, selectedTag.tag_key)}
						onCopy={(text) => copyToClipboard(text, "Impression snippet")}
					/>
					<SnippetRow
						title="Click (anchor)"
						enabled={selectedTag.events.includes("click")}
						snippet={clickSnippet(snippetBase, selectedTag.tag_key)}
						onCopy={(text) => copyToClipboard(text, "Click snippet")}
					/>
					<SnippetRow
						title="Conversion (script)"
						enabled={selectedTag.events.includes("conversion")}
						snippet={conversionSnippet(snippetBase, selectedTag.tag_key)}
						onCopy={(text) => copyToClipboard(text, "Conversion snippet")}
					/>

					<div className="mt-6 flex flex-col gap-3">
						<h3 className="text-sm font-semibold text-zinc-900">
							Recent fires
						</h3>
						<RecentEvents
							loading={eventsQuery.isLoading}
							error={eventsQuery.isError ? "Unable to load events." : null}
							events={eventsQuery.data ?? []}
							fireUrl={buildTagUrl(
								snippetBase,
								selectedTag.tag_key,
								"click",
							)}
						/>
					</div>
				</section>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Local presentational widgets
// ---------------------------------------------------------------------------

function SnippetRow(props: {
	title: string;
	enabled: boolean;
	snippet: string;
	onCopy: (text: string) => void;
}) {
	return (
		<div className="mt-4 flex flex-col gap-2">
			<div className="flex items-center justify-between gap-2">
				<span className="text-sm font-medium text-zinc-900">
					{props.title}
					{!props.enabled ? (
						<span className="ml-2 text-xs text-zinc-500">
							(not in this tag's event whitelist)
						</span>
					) : null}
				</span>
				<button
					type="button"
					className="rounded-[6px] border border-zinc-300 bg-white px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
					onClick={() => props.onCopy(props.snippet)}
				>
					Copy
				</button>
			</div>
			<pre className={CODE_BLOCK}>{props.snippet}</pre>
		</div>
	);
}

function RecentEvents(props: {
	loading: boolean;
	error: string | null;
	events: readonly UniversalTagEventView[];
	fireUrl: string;
}) {
	if (props.loading) {
		return <p className="text-sm text-zinc-500">Loading events…</p>;
	}
	if (props.error) {
		return <p className="text-sm text-red-600">{props.error}</p>;
	}
	if (props.events.length === 0) {
		return (
			<div className="rounded-[6px] border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm text-zinc-600">
				No fires yet. Try hitting{" "}
				<span className="break-all font-mono text-xs">{props.fireUrl}</span> in
				a new tab to confirm the tag is wired up.
			</div>
		);
	}
	return (
		<div className="overflow-hidden rounded-[6px] border border-zinc-200">
			<table className="w-full text-sm">
				<thead>
					<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
						<th className="px-4 py-2 font-medium">When</th>
						<th className="px-4 py-2 font-medium">Event</th>
						<th className="px-4 py-2 font-medium">Value</th>
						<th className="px-4 py-2 font-medium">Ref</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-100">
					{props.events.map((e) => (
						<tr key={e.id}>
							<td className="px-4 py-2 text-zinc-700">
								{formatFiredAt(e.fired_at)}
							</td>
							<td className="px-4 py-2">
								<span
									className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${eventBadgeClass(e.event)}`}
								>
									{EVENT_LABELS[e.event]}
								</span>
							</td>
							<td className="px-4 py-2 font-mono text-xs text-zinc-700">
								{e.value !== null
									? `${e.value.toFixed(2)}${e.currency ? " " + e.currency : ""}`
									: "—"}
							</td>
							<td className="break-all px-4 py-2 font-mono text-xs text-zinc-600">
								{e.ref ?? "—"}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default UniversalTagPage;
